import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from './utils/authUtils';

const BACKEND_URL = API_URL;

const RecruiterJobPostings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobPostings, setJobPostings] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Modal and form state
  const [showJobModal, setShowJobModal] = useState(false);
  const [showWorkdayModal, setShowWorkdayModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Workday integration state
  const [workdayConnected, setWorkdayConnected] = useState(false);
  const [workdayJobs, setWorkdayJobs] = useState([]);
  const [selectedWorkdayJobs, setSelectedWorkdayJobs] = useState(new Set());
  const [workdayLoading, setWorkdayLoading] = useState(false);
  const [importingJobs, setImportingJobs] = useState(false);
  
  // Form data
  const [jobFormData, setJobFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    responsibilities: '',
    city: '',
    state: '',
    country: 'United States',
    location: '',
    remote_type: '',
    industry: '',
    industry_other: '',
    employment_type: '',
    experience_level: '',
    salary_min: '',
    salary_max: '',
    salary_currency: 'USD',
    salary_period: 'annual',
    show_salary: false,
    benefits: '',
    application_method: 'external',
    application_url: '',
    application_email: '',
    department: '',
    category: '',
    keywords: []
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const PAGE_SIZE = 10;

  // Dropdown options
  const INDUSTRIES = [
    'Technology', 'Healthcare', 'Finance', 'Manufacturing', 'Retail', 
    'Education', 'Government', 'Non-Profit', 'Consulting', 'Energy',
    'Telecommunications', 'Transportation', 'Hospitality', 'Real Estate',
    'Media & Entertainment', 'Pharmaceutical', 'Aerospace & Defense',
    'Automotive', 'Construction', 'Agriculture', 'Legal', 'Insurance',
    'Food & Beverage', 'Other'
  ];

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia',
    'Puerto Rico', 'Remote'
  ];

  const getAuthToken = () => {
    return localStorage.getItem('recruiterToken') || sessionStorage.getItem('recruiterToken') || 
           localStorage.getItem('token') || sessionStorage.getItem('token');
  };

  const checkWorkdayConnection = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/workday/connection-status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('Workday connection response:', response.data);
      setWorkdayConnected(response.data.connected);
    } catch (err) {
      console.error('Error checking Workday connection:', err);
      if (err.response && err.response.data) {
        console.error('Server error details:', err.response.data);
      }
      setWorkdayConnected(false);
    }
  };

  useEffect(() => {
    fetchJobPostings();
    checkWorkdayConnection();
  }, [currentPage, filters]);

  const fetchJobPostings = async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: PAGE_SIZE.toString()
      });

      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }

      const response = await axios.get(
        `${BACKEND_URL}/api/job-postings?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setJobPostings(response.data.jobs);
      setTotalJobs(response.data.total);
      setTotalPages(Math.ceil(response.data.total / PAGE_SIZE));
      
    } catch (err) {
      console.error('Error fetching job postings:', err);
      setError('Failed to fetch job postings');
      if (err.response?.status === 401) {
        navigate('/recruiter/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = () => {
    setEditingJob(null);
    setJobFormData({
      title: '',
      description: '',
      requirements: '',
      responsibilities: '',
      city: '',
      state: '',
      country: 'United States',
      location: '',
      remote_type: '',
      industry: '',
      industry_other: '',
      employment_type: '',
      experience_level: '',
      salary_min: '',
      salary_max: '',
      salary_currency: 'USD',
      salary_period: 'annual',
      show_salary: false,
      benefits: '',
      application_method: 'external',
      application_url: '',
      application_email: '',
      department: '',
      category: '',
      keywords: []
    });
    setShowJobModal(true);
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setJobFormData({
      title: job.title || '',
      description: job.description || '',
      requirements: job.requirements || '',
      responsibilities: job.responsibilities || '',
      city: job.city || '',
      state: job.state || '',
      country: job.country || 'United States',
      location: job.location || '',
      remote_type: job.remote_type || '',
      industry: job.industry || '',
      industry_other: job.industry_other || '',
      employment_type: job.employment_type || '',
      experience_level: job.experience_level || '',
      salary_min: job.salary_min ? job.salary_min.toString() : '',
      salary_max: job.salary_max ? job.salary_max.toString() : '',
      salary_currency: job.salary_currency || 'USD',
      salary_period: job.salary_period || 'annual',
      show_salary: job.show_salary || false,
      benefits: job.benefits || '',
      application_method: job.application_method || 'external',
      application_url: job.application_url || '',
      application_email: job.application_email || '',
      department: job.department || '',
      category: job.category || '',
      keywords: job.keywords || []
    });
    setShowJobModal(true);
  };

  const handleSubmitJob = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      const token = getAuthToken();
      const processedKeywords = typeof jobFormData.keywords === 'string' 
        ? jobFormData.keywords.split(',').map(k => k.trim()).filter(k => k)
        : (Array.isArray(jobFormData.keywords) ? jobFormData.keywords.filter(k => k && k.trim()) : []);
      
      const payload = {
        ...jobFormData,
        salary_min: jobFormData.salary_min ? parseFloat(jobFormData.salary_min) : null,
        salary_max: jobFormData.salary_max ? parseFloat(jobFormData.salary_max) : null,
        keywords: processedKeywords.length > 0 ? processedKeywords : null  // Send null instead of empty array
      };

      if (editingJob) {
        await axios.put(
          `${BACKEND_URL}/api/job-postings/${editingJob.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccessMessage('Job posting updated successfully');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/job-postings`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccessMessage('Job posting created successfully');
      }

      setShowJobModal(false);
      fetchJobPostings();
      
    } catch (err) {
      console.error('Error saving job posting:', err);
      setError(err.response?.data?.detail || 'Failed to save job posting');
    } finally {
      setFormLoading(false);
    }
  };

  const handlePublishJob = async (jobId) => {
    try {
      const token = getAuthToken();
      await axios.post(
        `${BACKEND_URL}/api/job-postings/${jobId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage('Job posting published successfully');
      fetchJobPostings();
    } catch (err) {
      console.error('Error publishing job:', err);
      setError(err.response?.data?.detail || 'Failed to publish job posting');
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) {
      return;
    }

    try {
      const token = getAuthToken();
      await axios.delete(
        `${BACKEND_URL}/api/job-postings/${jobId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage('Job posting deleted successfully');
      fetchJobPostings();
    } catch (err) {
      console.error('Error deleting job:', err);
      setError(err.response?.data?.detail || 'Failed to delete job posting');
    }
  };

  const fetchWorkdayJobs = async () => {
    try {
      setWorkdayLoading(true);
      const token = getAuthToken();
      
      const response = await axios.get(
        `${BACKEND_URL}/api/workday/jobs`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setWorkdayJobs(response.data.jobs);
      } else {
        setError('Unable to fetch jobs from Workday: ' + response.data.connection_status);
      }
    } catch (err) {
      console.error('Error fetching Workday jobs:', err);
      setError('Failed to fetch jobs from Workday');
    } finally {
      setWorkdayLoading(false);
    }
  };

  const handleWorkdayImport = () => {
    setSelectedWorkdayJobs(new Set());
    setShowWorkdayModal(true);
    fetchWorkdayJobs();
  };

  const toggleWorkdayJobSelection = (jobId) => {
    const newSelection = new Set(selectedWorkdayJobs);
    if (newSelection.has(jobId)) {
      newSelection.delete(jobId);
    } else {
      newSelection.add(jobId);
    }
    setSelectedWorkdayJobs(newSelection);
  };

  const importSelectedJobs = async () => {
    if (selectedWorkdayJobs.size === 0) {
      setError('Please select at least one job to import');
      return;
    }

    try {
      setImportingJobs(true);
      const token = getAuthToken();
      
      const response = await axios.post(
        `${BACKEND_URL}/api/workday/import-jobs`,
        {
          workday_job_ids: Array.from(selectedWorkdayJobs)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowWorkdayModal(false);
        fetchJobPostings(); // Refresh job listings
      } else {
        setError('Failed to import jobs from Workday');
      }
    } catch (err) {
      console.error('Error importing jobs:', err);
      setError(err.response?.data?.detail || 'Failed to import jobs from Workday');
    } finally {
      setImportingJobs(false);
    }
  };

  const filteredJobs = jobPostings.filter(job => {
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return job.title.toLowerCase().includes(searchLower) ||
             job.description.toLowerCase().includes(searchLower) ||
             (job.company?.name || '').toLowerCase().includes(searchLower);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-2">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/recruiter/dashboard')}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreateJob}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Job Posting
              </button>
              
              {workdayConnected && (
                <button
                  onClick={handleWorkdayImport}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Import from Workday
                </button>
              )}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Job Postings
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your job postings and track applications
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {successMessage}
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search job postings..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Job Postings List */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No job postings found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {filters.search || filters.status !== 'all' 
                ? 'No job postings match your current filters.' 
                : 'Get started by creating your first job posting.'
              }
            </p>
            {(!filters.search && filters.status === 'all') && (
              <button
                onClick={handleCreateJob}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Your First Job Posting
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredJobs.map((job) => (
              <div key={job.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {job.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        job.status === 'active' ? 'bg-green-100 text-green-800' :
                        job.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                        job.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      <div className="flex flex-wrap gap-4">
                        {job.company?.name && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            {job.company.name}
                          </span>
                        )}
                        {(job.city || job.state) && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {[job.city, job.state].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {job.employment_type && (
                          <span className="flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {job.employment_type.charAt(0).toUpperCase() + job.employment_type.slice(1).replace('-', ' ')}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300 mb-3 line-clamp-2">
                      {job.description}
                    </p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>Views: {job.views_count}</span>
                      <span>Applications: {job.applications_count}</span>
                      <span>Created: {new Date(job.created_at).toLocaleDateString()}</span>
                      {job.posted_date && (
                        <span>Published: {new Date(job.posted_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={() => handleEditJob(job)}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      Edit
                    </button>
                    
                    {job.status === 'draft' && (
                      <button
                        onClick={() => handlePublishJob(job.id)}
                        className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        Publish
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleDeleteJob(job.id)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              
              <span className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Workday Import Modal */}
      {showWorkdayModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Import Jobs from Workday
                </h2>
                <button
                  onClick={() => setShowWorkdayModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {workdayLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-300">Loading jobs from Workday...</span>
                </div>
              ) : workdayJobs.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No jobs found</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    No jobs are available to import from your Workday system at this time.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      Select the jobs you want to import from Workday. They will be imported as drafts and you can edit them before publishing.
                    </p>
                  </div>
                  
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {workdayJobs.map((job) => (
                      <div key={job.workday_job_id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedWorkdayJobs.has(job.workday_job_id)}
                            onChange={() => toggleWorkdayJobSelection(job.workday_job_id)}
                            className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                              {job.title}
                            </h3>
                            <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                              {job.location && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  </svg>
                                  {job.location}
                                </span>
                              )}
                              {job.department && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                  </svg>
                                  {job.department}
                                </span>
                              )}
                              {job.employment_type && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  {job.employment_type}
                                </span>
                              )}
                              {job.posted_date && (
                                <span className="flex items-center gap-1">
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  Posted {new Date(job.posted_date).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                            {job.description && (
                              <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                                {job.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {workdayJobs.length > 0 && (
              <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 px-6 py-4">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedWorkdayJobs.size} of {workdayJobs.length} jobs selected
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowWorkdayModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md dark:bg-gray-600 dark:text-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={importSelectedJobs}
                      disabled={selectedWorkdayJobs.size === 0 || importingJobs}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md flex items-center gap-2"
                    >
                      {importingJobs ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                        </svg>
                      )}
                      Import Selected Jobs ({selectedWorkdayJobs.size})
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Job Posting Modal */}
      {showJobModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600 px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {editingJob ? 'Edit Job Posting' : 'Create Job Posting'}
                </h2>
                <button
                  onClick={() => setShowJobModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmitJob} className="p-6 space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={jobFormData.title}
                    onChange={(e) => setJobFormData({...jobFormData, title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., Software Engineer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Job Description *
                  </label>
                  <textarea
                    required
                    rows={4}
                    value={jobFormData.description}
                    onChange={(e) => setJobFormData({...jobFormData, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Describe the role, responsibilities, and what makes this opportunity exciting..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Department
                    </label>
                    <input
                      type="text"
                      value={jobFormData.department}
                      onChange={(e) => setJobFormData({...jobFormData, department: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Engineering"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category
                    </label>
                    <input
                      type="text"
                      value={jobFormData.category}
                      onChange={(e) => setJobFormData({...jobFormData, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Technology"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Location</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Work Type
                  </label>
                  <select
                    value={jobFormData.remote_type}
                    onChange={(e) => setJobFormData({...jobFormData, remote_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select work type</option>
                    <option value="onsite">On-site</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>

                {jobFormData.remote_type !== 'remote' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        City
                      </label>
                      <input
                        type="text"
                        value={jobFormData.city}
                        onChange={(e) => setJobFormData({...jobFormData, city: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        State
                      </label>
                      <select
                        value={jobFormData.state}
                        onChange={(e) => setJobFormData({...jobFormData, state: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select state</option>
                        {US_STATES.map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Country
                      </label>
                      <select
                        value={jobFormData.country}
                        onChange={(e) => setJobFormData({...jobFormData, country: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                )}

                {jobFormData.country === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location Details
                    </label>
                    <input
                      type="text"
                      value={jobFormData.location}
                      onChange={(e) => setJobFormData({...jobFormData, location: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter specific location"
                    />
                  </div>
                )}
              </div>

              {/* Employment Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Employment Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Employment Type
                    </label>
                    <select
                      value={jobFormData.employment_type}
                      onChange={(e) => setJobFormData({...jobFormData, employment_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select type</option>
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                      <option value="temporary">Temporary</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Experience Level
                    </label>
                    <select
                      value={jobFormData.experience_level}
                      onChange={(e) => setJobFormData({...jobFormData, experience_level: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select level</option>
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                      <option value="lead">Lead/Principal</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Industry
                  </label>
                  <select
                    value={jobFormData.industry}
                    onChange={(e) => setJobFormData({...jobFormData, industry: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select industry</option>
                    {INDUSTRIES.map(industry => (
                      <option key={industry} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>

                {jobFormData.industry === 'Other' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Other Industry
                    </label>
                    <input
                      type="text"
                      value={jobFormData.industry_other}
                      onChange={(e) => setJobFormData({...jobFormData, industry_other: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Please specify"
                    />
                  </div>
                )}
              </div>

              {/* Requirements & Responsibilities */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Details</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Requirements
                  </label>
                  <textarea
                    rows={3}
                    value={jobFormData.requirements}
                    onChange={(e) => setJobFormData({...jobFormData, requirements: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="List the required qualifications, skills, and experience..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Responsibilities
                  </label>
                  <textarea
                    rows={3}
                    value={jobFormData.responsibilities}
                    onChange={(e) => setJobFormData({...jobFormData, responsibilities: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Describe key responsibilities and day-to-day tasks..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Benefits
                  </label>
                  <textarea
                    rows={2}
                    value={jobFormData.benefits}
                    onChange={(e) => setJobFormData({...jobFormData, benefits: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Health insurance, 401k, flexible hours, etc..."
                  />
                </div>
              </div>

              {/* Compensation */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Compensation</h3>
                
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="show_salary"
                    checked={jobFormData.show_salary}
                    onChange={(e) => setJobFormData({...jobFormData, show_salary: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="show_salary" className="text-sm text-gray-700 dark:text-gray-300">
                    Display salary range publicly
                  </label>
                </div>

                {jobFormData.show_salary && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Minimum Salary
                      </label>
                      <input
                        type="number"
                        value={jobFormData.salary_min}
                        onChange={(e) => setJobFormData({...jobFormData, salary_min: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="50000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Maximum Salary
                      </label>
                      <input
                        type="number"
                        value={jobFormData.salary_max}
                        onChange={(e) => setJobFormData({...jobFormData, salary_max: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="80000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Currency
                      </label>
                      <select
                        value={jobFormData.salary_currency}
                        onChange={(e) => setJobFormData({...jobFormData, salary_currency: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="USD">USD</option>
                        <option value="CAD">CAD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Period
                      </label>
                      <select
                        value={jobFormData.salary_period}
                        onChange={(e) => setJobFormData({...jobFormData, salary_period: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="annual">Annual</option>
                        <option value="monthly">Monthly</option>
                        <option value="hourly">Hourly</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Application Settings */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Application Settings</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Application Method
                  </label>
                  <select
                    value={jobFormData.application_method}
                    onChange={(e) => setJobFormData({...jobFormData, application_method: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="external">External Link</option>
                    <option value="internal">Internal Application</option>
                  </select>
                </div>

                {jobFormData.application_method === 'external' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Application URL *
                    </label>
                    <input
                      type="url"
                      required={jobFormData.application_method === 'external'}
                      value={jobFormData.application_url}
                      onChange={(e) => setJobFormData({...jobFormData, application_url: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="https://company.com/careers/apply"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={jobFormData.application_email}
                    onChange={(e) => setJobFormData({...jobFormData, application_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="hiring@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={Array.isArray(jobFormData.keywords) ? jobFormData.keywords.join(', ') : jobFormData.keywords}
                    onChange={(e) => setJobFormData({...jobFormData, keywords: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="React, JavaScript, Node.js, AWS"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => setShowJobModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {formLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {editingJob ? 'Update Job Posting' : 'Create Job Posting'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecruiterJobPostings;